import { Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer';
import { ShippixShipment } from '../../types/shippix';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import JsBarcode from 'jsbarcode';

Font.register({
  family: 'Helvetica',
  fonts: [
    { src: 'Helvetica' },
    { src: 'Helvetica-Bold', fontWeight: 'bold' }
  ]
});

const generateBarcode = (value: string): string => {
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, value, {
    format: 'CODE128',
    width: 2,
    height: 70,
    displayValue: false,
    margin: 0,
  });
  return canvas.toDataURL();
};

const styles = StyleSheet.create({
  page: {
    padding: '20 30 20 30',
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  manifestSection: {
    height: '48%',
    marginBottom: '4%',
    position: 'relative',
  },
  header: {
    backgroundColor: 'rgb(10,25,47)',
    padding: '12 15',
    borderRadius: 4,
    marginBottom: 15,
    minHeight: 95,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: 'rgb(10,25,47)',
  },
  headerLeft: {
    width: '60%',
    justifyContent: 'space-between',
  },
  headerRight: {
    width: '40%',
    alignItems: 'flex-end',
  },
  logo: {
    width: 100,
    height: 25,
    objectFit: 'contain',
    marginBottom: 4,
  },
  companyInfo: {
    fontSize: 8,
    lineHeight: 1.2,
    textAlign: 'right',
    color: '#ffffff',
    marginBottom: 2,
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
  },
  trackingCode: {
    fontSize: 10,
    color: '#ffffff',
  },
  barcode: {
    height: 40,
    marginVertical: 4,
  },
  infoBlock: {
    marginBottom: 8,
    border: '1px solid #e2e8f0',
    borderRadius: 4,
    backgroundColor: '#ffffff',
  },
  lastInfoBlock: {
    marginBottom: 15,
    border: '1px solid #e2e8f0',
    borderRadius: 4,
    backgroundColor: '#ffffff',
  },
  infoBlockTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    color: 'rgb(10,25,47)',
    borderBottom: '1px solid #e2e8f0',
    padding: '3 8',
    backgroundColor: '#f8fafc',
    marginTop: -1,
  },
  infoText: {
    fontSize: 9,
    color: '#4a5568',
    padding: '2 8',
    lineHeight: 1.4,
  },
  mainContent: {
    flexDirection: 'row',
    gap: 10,
  },
  leftColumn: {
    flex: 1,
  },
  rightColumn: {
    width: '30%',
  },
  volumesBlock: {
    backgroundColor: 'rgb(10,25,47)',
    borderRadius: 4,
    padding: 8,
  },
  volumeItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  volumeLabel: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  volumeValue: {
    fontSize: 9,
    color: '#ffffff',
  },
  signatureBlock: {
    marginTop: 15,
  },
  signatureLine: {
    width: '100%',
    borderBottom: '1px solid #e2e8f0',
    marginTop: 20,
  },
  signatureLabel: {
    fontSize: 8,
    marginTop: 4,
    textAlign: 'center',
    color: '#4a5568',
    textTransform: 'uppercase',
  },
  footer: {
    position: 'absolute',
    bottom: -10,
    left: 0,
    right: 0,
    fontSize: 7,
    color: '#718096',
    textAlign: 'center',
  },
  divider: {
    position: 'absolute',
    bottom: -15,
    left: -30,
    right: -30,
    borderBottom: '2px dashed #e2e8f0',
  },
});

const ManifestSection = ({ shipment }: { shipment: ShippixShipment }) => (
  <View style={styles.manifestSection}>
    <View style={styles.header}>
      <View style={styles.headerContent}>
        <View style={styles.headerLeft}>
          <Text style={styles.title}>MANIFESTO DE RECOLHA</Text>
          <View style={styles.barcode}>
            <Image src={generateBarcode(shipment.tracking_code)} />
          </View>
          <Text style={styles.trackingCode}>Nº PEDIDO# {shipment.tracking_code}</Text>
        </View>
        <View style={styles.headerRight}>
          <Image src="/images/lognow-logo.png" style={styles.logo} />
          <Text style={styles.companyInfo}>NIF: 515240834</Text>
          <Text style={styles.companyInfo}>Rua da Lavandeira 1407 4485-704</Text>
          <Text style={styles.companyInfo}>Vila do Conde</Text>
          <Text style={styles.companyInfo}>Telef: 221453060</Text>
        </View>
      </View>
    </View>

    <View style={styles.infoBlock}>
      <Text style={styles.infoBlockTitle}>DATA/HORA PEDIDO</Text>
      <Text style={styles.infoText}>{format(new Date(shipment.date), 'yyyy-MM-dd / HH:mm', { locale: ptBR })}</Text>
      <Text style={styles.infoText}>Serviço: {shipment.service?.name || 'N/A'}</Text>
    </View>

    <View style={styles.mainContent}>
      <View style={styles.leftColumn}>
        <View style={styles.infoBlock}>
          <Text style={styles.infoBlockTitle}>SOLICITADO POR</Text>
          <Text style={styles.infoText}>{shipment.sender_name}</Text>
          <Text style={styles.infoText}>{shipment.sender_address}</Text>
          <Text style={styles.infoText}>{shipment.sender_postal_code} {shipment.sender_city} (PT)</Text>
        </View>

        <View style={styles.infoBlock}>
          <Text style={styles.infoBlockTitle}>EXPEDIDOR (LOCAL RECOLHA)</Text>
          <Text style={styles.infoText}>{shipment.sender_name}</Text>
          <Text style={styles.infoText}>{shipment.sender_address}</Text>
          <Text style={styles.infoText}>{shipment.sender_postal_code} {shipment.sender_city} (PT)</Text>
          <Text style={styles.infoText}>Tlf: {shipment.sender_phone || 'N/A'}</Text>
        </View>

        <View style={styles.infoBlock}>
          <Text style={styles.infoBlockTitle}>DESTINATÁRIO</Text>
          <Text style={styles.infoText}>{shipment.recipient_name}</Text>
          <Text style={styles.infoText}>{shipment.recipient_address}</Text>
          <Text style={styles.infoText}>{shipment.recipient_postal_code} {shipment.recipient_city} (PT)</Text>
          <Text style={styles.infoText}>N.º Cliente: {shipment.recipient_customer_number || 'N/A'}</Text>
          <Text style={styles.infoText}>Tlf.: {shipment.recipient_phone || 'N/A'}</Text>
        </View>

        <View style={styles.lastInfoBlock}>
          <Text style={styles.infoBlockTitle}>OBSERVAÇÕES</Text>
          <Text style={styles.infoText}>{shipment.observations || 'Sem observações'}</Text>
        </View>
      </View>

      <View style={styles.rightColumn}>
        <View style={styles.volumesBlock}>
          <View style={styles.volumeItem}>
            <Text style={styles.volumeLabel}>VOLUMES</Text>
            <Text style={styles.volumeValue}>1</Text>
          </View>
          <View style={styles.volumeItem}>
            <Text style={styles.volumeLabel}>PESO</Text>
            <Text style={styles.volumeValue}>{typeof shipment.weight === 'number' ? `${shipment.weight.toFixed(2)}kg` : '0.00kg'}</Text>
          </View>
          <View style={styles.volumeItem}>
            <Text style={styles.volumeLabel}>COBRANÇA</Text>
            <Text style={styles.volumeValue}>______</Text>
          </View>
        </View>

        <View style={styles.signatureBlock}>
          <Text style={styles.signatureLabel}>ASSINATURA TRANSPORTADOR</Text>
          <Text style={styles.signatureLine}></Text>
        </View>

        <View style={styles.signatureBlock}>
          <Text style={styles.signatureLabel}>ASSINATURA EXPEDIDOR</Text>
          <Text style={styles.signatureLine}></Text>
        </View>
      </View>
    </View>

    <View style={styles.footer}>
      <Text>LOGNOW - Solução All-in-One para E-commerce</Text>
      <Text>Documento processado por computador / Despacho DGTT N.º21.994/99 (2ª série)</Text>
    </View>

    <View style={styles.divider} />
  </View>
);

export default function PickupManifest({ shipment }: { shipment: ShippixShipment }) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ManifestSection shipment={shipment} />
        <ManifestSection shipment={shipment} />
      </Page>
    </Document>
  );
}
